// CODICE api\earthquake-crud.ts

import { type EarthquakeWithPagination } from '@/models/types/earthquake/earthquake-with-pagination'
import { type Earthquake } from '@/models/types/earthquake/earthquake'
import { type EarthquakeDTO } from '@/models/dto/earthquake/earthquake-dto'
import { type EarthquakePaginationDTO } from '@/models/dto/earthquake/earthquake-pagination-dto'
import {
    mapToEarthquake,
    mapToEarthquakes,
    mapToEarthquakesWithPagination,
} from '@/models/mappers/earthquake/earthquake-mapper'
import { capitalizeFirstLetter } from '@/utils/utils'
import type { OrderType } from '@/types/table'
import { type FetchOptionsType } from './api'
import { getRequest, putJsonRequest } from './make-request'
import internal from 'stream'

export type EarthquakeGetOptions = {
    order?: OrderType
    sort?: keyof Earthquake
    search?: string
    page?: string
}

export type PutNotaRequest = {
    id: number
    nota: string
}

// Tipo dell'oggetto CRUD
export type EarthquakeCrudType = (fetchOptions?: FetchOptionsType) => {
    getOne: (id: number) => Promise<Earthquake>
    getAll: () => Promise<Earthquake[]>
    putNota: (notareq: PutNotaRequest) => Promise<Earthquake>
    getMany: (
        itemsPerPage?: string,
        options?: EarthquakeGetOptions,
        onlyEnabled?: boolean,
    ) => Promise<EarthquakeWithPagination>
}

// URL base per le chiamate ai "Sismi"
const EARTHQUAKE_URL =
    typeof window === 'undefined' ? `${process.env.API_URL}/api/v1/Sisma` : `${location.origin}/api/v1/Sisma`

console.log('URL EARTHQUAKE', EARTHQUAKE_URL)
// Tag di cache (se lo utilizzi in Next.js)
export const EARTHQUAKE_TAG = 'earthquake'

export const earthquakeCRUD: EarthquakeCrudType = (fetchOptions) => ({
    /**
     * Recupera un singolo sisma /api/v1/Sisma/:id
     */
    getOne: async (id) => {
        const url = new URL(`${EARTHQUAKE_URL}/${String(id)}`)
        const dto = await getRequest<EarthquakeDTO>({
            url,
            next: { tags: [EARTHQUAKE_TAG] },
            ...fetchOptions,
        })
        return mapToEarthquake(dto)
    },

    /**
     * Recupera tutti i sismi (se l'endpoint `GET /api/v1/Sisma` li restituisce tutti)
     */
    getAll: async () => {
        const url = new URL(EARTHQUAKE_URL)
        const dtos = await getRequest<EarthquakeDTO[]>({
            url,
            next: { tags: [EARTHQUAKE_TAG] },
            ...fetchOptions,
        })
        return mapToEarthquakes(dtos)
    },
    putNota: async (notareq) => {
        const url = new URL(EARTHQUAKE_URL+ "/Nota")
        const dto = await putJsonRequest<EarthquakeDTO>({
            url,
            next: { tags: [EARTHQUAKE_TAG] },
            body: notareq,
            ...fetchOptions,
        })
        return mapToEarthquake(dto)
    },


    /**
     * Recupera i sismi con paginazione e filtri
     * (usa /api/v1/Sisma/Pagination o l'endpoint che preferisci)
     */
    getMany: async (itemsPerPage, options, onlyEnabled) => {
        const url = new URL(`${EARTHQUAKE_URL}/Pagination`)
        url.searchParams.append('PageSize', itemsPerPage ?? '999999')

        if (options?.page) {
            url.searchParams.append('PageIndex', options.page)
        }
        if (options?.order) {
            url.searchParams.append('Descending', options.order === 'desc' ? 'true' : 'false')
        }
        if (options?.sort) {
            // Trasforma la chiave "sort" in un formato accettato dal tuo backend
            url.searchParams.append('SortBy', capitalizeFirstLetter(options.sort))
        }
        if (onlyEnabled) {
            url.searchParams.append('OnlyActive', 'true')
        }
        if (options?.search) {
            url.searchParams.append('Filters[search]', options.search)
        }

        // Eventuali altri filtri se ti servono (es. dataInizio, dataFine)
        // es: if (options?.dataInizio) url.searchParams.append('Filters[dataInizio]', options.dataInizio)

        // Esegui la chiamata
        const paginationDto = await getRequest<EarthquakePaginationDTO>({
            url,
            next: { tags: [EARTHQUAKE_TAG] },
            ...fetchOptions,
        })
        console.log('paginationDto:', paginationDto)

        // Mappa la struttura paginata in EarthquakeWithPagination
        return mapToEarthquakesWithPagination(paginationDto)
    },
})
